import { toast } from '@/components/ui/toast';
import { useCurrentUserProfile, useUpdateProfile } from '@/hooks/use-profile';
import { useUsernameAvailability } from '@/hooks/use-username-availability';
import { RESERVED_USERNAMES } from '@/utils/auth-validation';
import { imageUrl } from '@/utils/image-url';
import { createLogger } from '@/utils/logger';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';

const logger = createLogger('EDIT_PROFILE_FORM');

export function useEditProfileForm() {
  const router = useRouter();
  const { data: profileData, isLoading: profileLoading } = useCurrentUserProfile();
  const updateProfile = useUpdateProfile();

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');

  const user = profileData?.user;

  const { usernameAvailable, checkingUsername } = useUsernameAvailability(username, originalUsername);

  useEffect(() => {
    if (user) {
      const currentUsername = (user.username || user.displayUsername || '').replace(/^@+/, '').toLowerCase();
      setName(user.name || '');
      setUsername(currentUsername);
      setOriginalUsername(currentUsername);
      setBio(user.bio || '');
      setProfileImage(imageUrl(user.image, 'thumbnail') || '');
    }
  }, [user]);

  const handleUsernameChange = useCallback((text: string) => {
    const formatted = text.toLowerCase().replace(/[^a-z0-9]/g, '');
    setUsername(formatted);
  }, []);

  const handleSave = useCallback(async () => {
    if (!user) return;

    const nextUsername = username.trim().toLowerCase();
    const usernameChanged = nextUsername !== originalUsername;

    if (nextUsername && usernameChanged) {
      if (nextUsername.length < 3) {
        toast.warning('Username must be at least 3 characters long');
        return;
      }
      if (nextUsername.length > 30) {
        toast.warning('Username must be less than 30 characters long');
        return;
      }
      if (!/^[a-zA-Z0-9]+$/.test(nextUsername)) {
        toast.warning('Username can only contain letters and numbers');
        return;
      }
      if (RESERVED_USERNAMES.includes(nextUsername as typeof RESERVED_USERNAMES[number])) {
        toast.warning('This username is reserved and cannot be used');
        return;
      }
      if (usernameAvailable === false) {
        toast.warning('Username is already taken');
        return;
      }
      if (checkingUsername) {
        toast('Still checking username availability...');
        return;
      }
    }

    try {
      await updateProfile.mutateAsync({
        name: name.trim(),
        username: nextUsername && usernameChanged ? nextUsername : undefined,
        bio: bio.trim(),
      });
      logger.log('Profile updated successfully');
      router.back();
    } catch {
      logger.error('Failed to save profile');
      toast.error('Failed to save profile changes');
    }
  }, [user, username, originalUsername, usernameAvailable, checkingUsername, name, bio, updateProfile, router]);

  return {
    name,
    setName,
    username,
    handleUsernameChange,
    bio,
    setBio,
    profileImage,
    setProfileImage,
    profileLoading,
    user,
    usernameAvailable,
    checkingUsername,
    originalUsername,
    isSaving: updateProfile.isPending,
    handleSave,
  };
}
